import { useState } from "react";

import { withTranslation } from "react-i18next";
import type { WithTranslation } from "react-i18next";

import { Button, Dropdown, Icon, Loader, Segment } from "simplydo/react/ui";

import DiscoverImage from "src/images/discover.png";

import Checkbox from "components/lib/Forms/Checkbox";

import { HandledIdeaPreview } from "../Ideas/IdeaPreview";
import type { IdeaPreviewChallenge } from "../Ideas/IdeaPreview/types";
import PostWidget from "./ActivityFeedPostWidget";
import AchievementCard from "./FeedCards/Achievement";
import ChallSubmitCard from "./FeedCards/ChallSubmitCard";
import ChallengeIdeaCommentCard from "./FeedCards/ChallengeIdeaComment";
import Comment from "./FeedCards/Comment";
import DiscoverChallengeCard from "./FeedCards/DiscoverChallenge";
import GroupDiscussionFeed from "./FeedCards/GroupDiscussionFeed";
import IdeaCard from "./FeedCards/Idea";
import OrganisationPostCard from "./FeedCards/OrganisationPost";
import SubmittedIdeaCard from "./FeedCards/SubmittedIdea";
import type {
  ActivityFeedViewProps,
  FeedAchievementItem,
  FeedActivityPostItem,
  FeedChallengeIdeaCommentItem,
  FeedCommentItem,
  FeedDiscoverChallengeItem,
  FeedFollowedChallengeIdeaItem,
  FeedGroupDiscussionItem,
  FeedGroupPostItem,
  FeedIdeaItem,
  FeedSubmittedIdeaItem,
  HomeFeedItem,
} from "./types";

const ActivityFeed = ({
  t,
  feed = [],
  show = [],
  loading,
  handleFeedType,
  feedOptions,
  extendFeed,
  canExtend,
  profileFeed,
  isGroupFeed,
  emptyFeedMessage,
  profileUser,
  hideAction,
  extending,
  canPost,
  onPost,
  onUpdatePost,
  onDeletePost,
}: ActivityFeedViewProps & WithTranslation) => {
  const [ideaPreviewId, setIdeaPreviewId] = useState("");
  let emptyMessage;
  if (emptyFeedMessage) emptyMessage = emptyFeedMessage;
  else
    emptyMessage = profileFeed
      ? "This user has no activity to display"
      : "When you start to receive notifications, they'll appear here.";
  const feedType =
    (profileFeed && "Profile") || (isGroupFeed ? "Group" : "Activity");
  return (
    <>
      <HandledIdeaPreview
        ideaId={ideaPreviewId || null}
        onCloseModal={() => setIdeaPreviewId("")}
        page={1}
        limit={1}
        updateProjectLane={() => {}}
        challenge={
          {
            _id: "",
            name: "",
            organisation: "",
            createdAt: "",
            updatedAt: "",
          } as IdeaPreviewChallenge
        }
      />
      {feedOptions && feedOptions.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginBottom: 15,
          }}
        >
          <h4 className="primary-text" style={{ marginBottom: 0 }}>
            <Dropdown
              as="a"
              style={{
                flexDirection: "row",
                display: "flex",
                fontWeight: "bold",
                textTransform: "uppercase",
                alignItems: "center",
              }}
              size="tiny"
              basic
              direction="right"
              trigger={
                <div>
                  <Icon name="rss" />
                  {t("generic.activityFeed").toUpperCase()}
                </div>
              }
            >
              {feedOptions.length ? (
                <Dropdown.Menu>
                  {feedOptions.map((o) => (
                    <Dropdown.Item key={o.value}>
                      <Checkbox
                        compact
                        key={o.value}
                        checked={show.includes(o.value)}
                        label={o.text}
                        onChange={() => handleFeedType?.(o)}
                      />
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              ) : null}
            </Dropdown>
          </h4>
        </div>
      )}

      {canPost && onPost && (
        <PostWidget onPost={onPost} isGroupFeed={isGroupFeed} />
      )}
      <Loader active={loading} />

      {!loading && (
        <>
          {feed.map((item, i) => {
            const feedItem = item as HomeFeedItem;
            switch (feedItem.type) {
              case "discoverChallenge":
                return (
                  <DiscoverChallengeCard
                    key={feedItem._id}
                    challenge={feedItem as FeedDiscoverChallengeItem}
                    hideAction={hideAction}
                    feedType={feedType}
                  />
                );
              case "achievement":
                return (
                  <AchievementCard
                    key={i}
                    achievement={feedItem as FeedAchievementItem}
                    profileUser={profileUser}
                    hideAction={hideAction}
                    feedType={feedType}
                  />
                );
              case "ideaComment":
                return (
                  <Comment
                    key={i}
                    comment={feedItem as FeedCommentItem}
                    profileFeed={profileFeed}
                    profileUser={profileUser}
                    hideAction={hideAction}
                    feedType={feedType}
                  />
                );
              case "challengeIdeaComment":
                return (
                  <ChallengeIdeaCommentCard
                    key={i}
                    comment={feedItem as FeedChallengeIdeaCommentItem}
                    profileFeed={profileFeed}
                    profileUser={profileUser}
                    hideAction={hideAction}
                    feedType={feedType}
                    setIdeaPreviewId={setIdeaPreviewId}
                  />
                );
              case "idea":
                return (
                  <IdeaCard
                    key={i}
                    idea={feedItem as FeedIdeaItem}
                    profileFeed={profileFeed}
                    hideAction={hideAction}
                    isGroupFeed={isGroupFeed}
                    feedType={feedType}
                  />
                );
              case "submittedIdea":
                return (
                  <SubmittedIdeaCard
                    key={i}
                    idea={feedItem as FeedSubmittedIdeaItem}
                    hideAction={hideAction}
                    feedType={feedType}
                  />
                );
              case "groupFeedComment":
                return (
                  <GroupDiscussionFeed
                    key={i}
                    comment={feedItem as FeedGroupDiscussionItem}
                    hideAction={hideAction}
                    feedType={feedType}
                  />
                );
              case "followedChallengeSubmittedIdea":
                return (
                  <ChallSubmitCard
                    key={i}
                    idea={feedItem as FeedFollowedChallengeIdeaItem}
                    profileFeed={profileFeed}
                    hideAction={hideAction}
                    feedType={feedType}
                    setIdeaPreviewId={setIdeaPreviewId}
                  />
                );
              case "organisationActivityFeedPost":
              case "groupActivityFeedPost": {
                const post = feedItem as FeedActivityPostItem;
                const groupPost =
                  post.type === "groupActivityFeedPost"
                    ? (post as FeedGroupPostItem)
                    : undefined;
                return (
                  <OrganisationPostCard
                    key={i}
                    post={post}
                    toLink={
                      groupPost
                        ? `/groups/${groupPost.ownerGroup?._id}/feed`
                        : null
                    }
                    canPost={
                      !!canPost &&
                      ((post.type === "groupActivityFeedPost" && isGroupFeed) ||
                        (post.type === "organisationActivityFeedPost" &&
                          !isGroupFeed))
                    }
                    onUpdatePost={onUpdatePost}
                    onDeletePost={onDeletePost}
                    isGroup={post.type === "groupActivityFeedPost"}
                    isGroupFeed={isGroupFeed}
                  />
                );
              }
              default:
                return null;
            }
          })}
          <div style={{ width: "100%" }}>
            {feed.length === 0 && (
              <Segment placeholder textAlign="center">
                <img
                  src={DiscoverImage}
                  style={{
                    maxHeight: 150,
                    maxWidth: "70%",
                    display: "block",
                    margin: "10px auto",
                  }}
                  alt="Discover"
                />
                <h2>{emptyMessage}</h2>
              </Segment>
            )}
            {extendFeed && canExtend && (
              <div
                style={{
                  justifyContent: "center",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Button
                  loading={extending}
                  content={t("activityFeed.extend")}
                  onClick={() => extendFeed()}
                  style={{ width: "40%" }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default withTranslation()(ActivityFeed);
